import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '@/services/apiService';
import { TourOverlay } from './TourOverlay';
import { useTourGuide } from './TourContext';
import { TourWelcomeModal } from './TourWelcomeModal';
import { userPreferencesService } from '@/services/userPreferencesService';
import { getTourForPath } from './pageTours';

export default function PostLoginTourGate() {
  const location = useLocation();
  const { status, openWelcome, start, close } = useTourGuide();
  const [shouldShowWelcome, setShouldShowWelcome] = useState(false);
  const [loading, setLoading] = useState(true);

  // Prevent re-opening multiple times in same session.
  const openedRef = useRef(false);

  const tour = useMemo(() => getTourForPath(location.pathname), [location.pathname]);
  const canRunOnThisRoute = tour != null;
  const hasToken = Boolean(apiService.getAccessToken());

  const refreshEligibility = useCallback(async () => {
    if (!hasToken || !canRunOnThisRoute) {
      setLoading(false);
      setShouldShowWelcome(false);
      return;
    }

    try {
      setLoading(true);
      const pref = await userPreferencesService.getTourPreference(tour!.id);
      const completed = pref?.version === tour!.version && pref?.completed === true;
      setShouldShowWelcome(!completed);
    } catch {
      // Fail-open (show), but keep a local guard so we don't spam.
      setShouldShowWelcome(true);
    } finally {
      setLoading(false);
    }
  }, [hasToken, canRunOnThisRoute, tour]);

  useEffect(() => {
    void refreshEligibility();
  }, [refreshEligibility]);

  useEffect(() => {
    openedRef.current = false;
  }, [tour?.id]);

  useEffect(() => {
    if (loading) return;
    if (!shouldShowWelcome) return;
    if (openedRef.current) return;
    if (!hasToken || !canRunOnThisRoute) return;
    if (status !== 'idle') return;

    openedRef.current = true;
    openWelcome(tour!.steps);
  }, [loading, shouldShowWelcome, hasToken, canRunOnThisRoute, status, openWelcome, tour]);

  const handleClose = useCallback(() => {
    userPreferencesService.dismissAllTourGuides();
    setShouldShowWelcome(false);
    close();
  }, [close]);

  const handleStart = useCallback(() => {
    start();
  }, [start]);

  const handleFinish = useCallback(async () => {
    try {
      if (tour) await userPreferencesService.setTourCompleted({ tourId: tour.id, version: tour.version });
      setShouldShowWelcome(false);
    } finally {
      close();
    }
  }, [close, tour]);

  return (
    <>
      <TourWelcomeModal
        isOpen={status === 'welcome'}
        title={tour?.welcomeTitle ?? 'Welcome'}
        description={tour?.welcomeDescription ?? ''}
        onClose={handleClose}
        onStart={handleStart}
      />
      <TourOverlay onRequestClose={handleClose} onRequestFinish={handleFinish} />
    </>
  );
}

